import { useEffect, useRef, useState } from 'react';

type Options = {
  enabled: boolean;
  onPoll: () => void;
  intervalMs: number;
  timeoutMs: number;
};

/**
 * Drives a polling loop while `enabled === true`. Calls `onPoll` every
 * `intervalMs` until either consumer disables polling (e.g. condition
 * resolves), the component unmounts, or `timeoutMs` elapses — in which case
 * `timedOut` flips to `true` and `onPoll` stops being called.
 */
export default function useProcessingPolling({
  enabled,
  onPoll,
  intervalMs,
  timeoutMs,
}: Options): { timedOut: boolean } {
  const [timedOut, setTimedOut] = useState(false);
  const onPollRef = useRef(onPoll);
  onPollRef.current = onPoll;

  useEffect(() => {
    if (!enabled) {
      setTimedOut(false);
      return undefined;
    }
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (Date.now() - startedAt >= timeoutMs) {
        setTimedOut(true);
        window.clearInterval(interval);
        return;
      }
      onPollRef.current();
    }, intervalMs);
    return () => window.clearInterval(interval);
  }, [enabled, intervalMs, timeoutMs]);

  return { timedOut };
}
